/* ============================================================================
   AuthModal - sign in / sign up dialog.
   ----------------------------------------------------------------------------
   Safe-by-default copy. Signup flow:
     Step 1: OAuth buttons OR email + password form (the global instance)
     Step 2: Trainer name and level, two typed fields on one row (TrainerSetup,
             built on components/TrainerFields). Rendered by a separate
             instance: the ProfileGate (straight after sign-up, from Live Scan,
             and once for every account that has never typed its name) and the
             web portal's gate. Settings edits the same two fields in place,
             with no dialog.
     (Friend code was removed from this step on 2026-07-16; the profile
      screenshot and its OCR on 2026-10-02, see lib/trainerSetup.)
   ============================================================================ */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogPortal, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "./AuthProvider";
import { loginWithGoogle, loginWithApple, isAppleSignInAvailable } from "@/lib/socialAuth";
import { deferTrainerConfirm, dismissProfileSetupRequest } from "@/lib/profileSetupRequest";
import { getPlatform } from "@/lib/platform";
import { authErrorMessage } from "@/lib/apiError";
import { TrainerFieldsRow, useTrainerForm } from "./TrainerFields";

// ── Small social icon components ──────────────────────────────────────────────

function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" />
    </svg>
  );
}

// ── Trainer setup step ────────────────────────────────────────────────────────
// Two typed fields on ONE row: the trainer's Pokémon GO name and level. No
// screenshot and no OCR (removed 2026-10-02, lib/trainerSetup has the why).
//
// COMPACT ON PURPOSE (owner direction, 2026-10-02): the step asks for two
// values, and it used to fill most of the screen to do it (a stacked form, a
// helper line under each box, safe-area padding on a centred card). It is now
// a short card: a heading, one line of why, the row, the button.

interface TrainerSetupProps {
  onComplete: () => void;
  /**
   * "confirm": a trainer from before the typed step, asked once to type (or
   * agree to) the name and level their account holds. "setup": a new trainer.
   */
  kind: "setup" | "confirm";
  /** "Not now". Only the ProfileGate passes it; nothing else can be put off. */
  onDefer?: () => void;
}

function TrainerSetup({ onComplete, kind, onDefer }: TrainerSetupProps) {
  // Either way the trainer is telling us who they are, so the save always
  // goes to the server: it is what marks the account as having typed it.
  const form = useTrainerForm({ confirming: true });
  const confirming = kind === "confirm";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await form.save()) onComplete();
  };

  // One line under the row, only when needed: what went wrong, or what is
  // still missing once they have started typing.
  const hint =
    form.error ??
    (!form.busy && form.missing.length > 0 && (form.trainerName.length > 0 || form.level.length > 0)
      ? `${form.missing[0]}.`
      : null);

  return (
    <>
      <div className="flex justify-center mb-1.5">
        <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />YOUR TRAINER</span>
      </div>
      <DialogHeader className="space-y-1">
        <DialogTitle className="v2-display text-lg leading-tight tracking-tight text-foreground">
          {confirming ? "Confirm your trainer name and level." : "Your trainer name and level."}
        </DialogTitle>
        <DialogDescription className="text-xs leading-snug text-muted-foreground">
          {confirming
            ? "We no longer keep profile screenshots. This is your Nexus Dex name from now on, and how friends find you."
            : "As they appear in Pokémon GO. Friends find you by this name."}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="mt-3" data-testid="form-trainer-setup" data-step-kind={kind}>
        <div className="flex items-center gap-2">
          <TrainerFieldsRow form={form} idPrefix="trainer-step" />
        </div>
        {/* Only when there is something to say: the placeholders and the boxes
            themselves (the name box drops anything but letters and digits as
            it is typed) carry the rules the rest of the time. */}
        {hint && (
          <p
            className={`mt-1.5 text-[11px] leading-snug ${form.error ? "text-destructive" : "text-muted-foreground"}`}
            data-testid={form.error ? "text-profile-error" : "profile-missing-requirements"}
          >
            {hint}
          </p>
        )}

        <div className="mt-3 flex items-center gap-3">
          {onDefer && (
            // NOT NOW. A full-screen step straight after sign-up is where new
            // trainers were lost when it asked for a screenshot. Two fields
            // cost far less, but the way out stays: it closes this until the
            // app is next opened, and Live Scan asks when they want to scan.
            <button
              type="button"
              onClick={onDefer}
              data-testid="button-profile-defer"
              className="shrink-0 px-1 text-[12px] font-semibold underline underline-offset-2 text-muted-foreground hover:text-foreground"
            >
              Not now
            </button>
          )}
          <button
            type="submit"
            disabled={!form.canSubmit}
            data-testid="button-profile-submit"
            className="v2-neon-cta flex-1 py-2.5 inline-flex items-center justify-center disabled:opacity-60"
          >
            {form.busy ? "Saving" : confirming ? "Confirm" : "Continue"}
          </button>
        </div>
      </form>
    </>
  );
}

// ── Main AuthModal ─────────────────────────────────────────────────────────────

interface AuthModalProps {
  /**
   * When true, the modal is the trainer step (name and level) and cannot be
   * closed by tapping away. The ProfileGate and the web portal's gate.
   */
  mandatoryMode?: boolean;
  /** Force the modal open from outside. */
  forceOpen?: boolean;
  /**
   * With mandatoryMode: offer "Not now". The ProfileGate passes it (the app is
   * usable without a trainer name; Live Scan asks again). The web portal's gate
   * does not, because nothing behind it renders until the name is set.
   */
  deferrable?: boolean;
  /**
   * With mandatoryMode: which face of the trainer step (the ProfileGate works
   * it out, lib/trainerSetup.trainerStepKind). "confirm" is the once-only ask
   * of a trainer from before the typed step. Defaults to "setup".
   */
  stepKind?: "setup" | "confirm";
}

export function AuthModal({
  mandatoryMode = false,
  forceOpen = false,
  deferrable = false,
  stepKind = "setup",
}: AuthModalProps) {
  const { isAuthOpen, closeAuth, authMode, signin, signup, openAuth, signInWithGoogle, signInWithApple, account } = useAuth();
  // Which face this instance shows is fixed by how it was mounted: the global
  // one is always sign in / sign up, a mandatory one is always the trainer
  // step. (They used to share a `step` the global instance walked through;
  // sign-up has handed the second step to the ProfileGate since 2026-10-01.
  // Settings had a dismissible "edit" instance until 2026-10-02; it now edits
  // the two fields in place, components/TrainerFields.)
  const trainerStep = mandatoryMode;
  // How much of the screen the keyboard covers, for the dialog's position
  // (see the DialogContent style). Only iOS overlays the page.
  const keyboardRoom = getPlatform() === "ios" ? "var(--keyboard-h, 0px)" : "0px";
  // What the trainer step keeps clear of at the bottom: the keyboard when it
  // is up, the home indicator when it is not.
  const bottomRoom = `max(${keyboardRoom}, var(--nx-safe-bottom, 0px))`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isUnder13, setIsUnder13] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthBusy, setOauthBusy] = useState<"google" | "apple" | null>(null);
  // Set when a sign-in attempt fails. Server returns the same generic 401 for
  // "no account" and "wrong password" so we don't know which it was, but we
  // surface a "Create account instead?" button either way to give the user a
  // recovery path without leaking which emails are registered.
  const [signinNotFound, setSigninNotFound] = useState(false);

  // Real OAuth handlers. On iOS / Android, @capgo/capacitor-social-login uses
  // the native Google Sign-In SDK and Apple AuthenticationServices. On web, it
  // falls back to Google Identity Services and Apple JS. There is no demo
  // path - if a provider can't return an ID token, we surface the error.
  const handleGoogleSignIn = async () => {
    setError(null);
    setOauthBusy("google");
    try {
      const res = await loginWithGoogle();
      await signInWithGoogle({
        idToken: res.idToken,
        email: res.email ?? "",
        displayName: res.displayName ?? undefined,
        subject: res.subject ?? undefined,
      });
      handleOAuthSuccess();
    } catch (e: unknown) {
      const msg = authErrorMessage(e, "Google sign-in failed");
      setError(msg);
    } finally {
      setOauthBusy(null);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setOauthBusy("apple");
    try {
      const res = await loginWithApple();
      await signInWithApple({
        idToken: res.idToken,
        email: res.email ?? "",
        displayName: res.displayName ?? undefined,
        subject: res.subject ?? undefined,
      });
      handleOAuthSuccess();
    } catch (e: unknown) {
      const msg = authErrorMessage(e, "Apple sign-in failed");
      setError(msg);
    } finally {
      setOauthBusy(null);
    }
  };

  const isOpen = isAuthOpen || forceOpen;
  const isSignup = authMode === "signup";

  const reset = () => {
    setEmail("");
    setPassword("");
    setDisplayName("");
    setIsUnder13(false);
    setParentalConsent(false);
    setBusy(false);
    setError(null);
  };

  // In mandatory mode, pressing ESC or clicking outside does nothing.
  const handleOpenChange = (o: boolean) => {
    if (!o && mandatoryMode) return; // Block close in mandatory mode
    if (!o) {
      reset();
      closeAuth();
    }
  };

  const handleClose = () => {
    if (mandatoryMode) return;
    reset();
    closeAuth();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSigninNotFound(false);
    try {
      if (isSignup) {
        if (isUnder13 && !parentalConsent) {
          setError("Please confirm a parent or guardian has approved.");
          setBusy(false);
          return;
        }
        await signup({
          email: email.trim(),
          password,
          displayName: displayName.trim() || undefined,
          isUnder13,
          parentalConsentConfirmed: isUnder13 ? parentalConsent : undefined,
        });
        // This dialog is done. A new account has no trainer name yet, so
        // AuthProvider has already asked for the trainer step and the
        // ProfileGate opens it over the app (lib/profileSetupRequest).
        handleClose();
        return;
      } else {
        await signin(email.trim(), password);
      }
      reset();
    } catch (err: unknown) {
      const msg = authErrorMessage(err);
      // Sign-in failure: server returns a generic 401 for both "no account"
      // and "wrong password" (anti-enumeration). Surface a recovery path so a
      // user who hit Sign In by mistake can convert to Sign Up without losing
      // what they typed.
      if (!isSignup) {
        setSigninNotFound(true);
      }
      setError(msg);
      setBusy(false);
    }
  };

  // Switch to sign-up while preserving the email + password the user just
  // typed in the sign-in form, so they don't have to re-enter anything.
  const switchToSignupKeepingEntries = () => {
    setError(null);
    setSigninNotFound(false);
    openAuth("signup");
  };

  const handleOAuthSuccess = () => {
    // This dialog is done. An account with no trainer name yet gets the
    // trainer step from the ProfileGate (AuthProvider asked for it).
    handleClose();
  };

  const handleProfileComplete = () => {
    reset();
    closeAuth();
    // From a gate: the trainer is on an app page (just signed up, or about to
    // scan), so leave them there. The name makes the gate moot; clearing the
    // request keeps the device's list tidy.
    dismissProfileSetupRequest(account?.id);
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={false}
      >
        {/* THE TRAINER STEP'S OWN SCRIM. The dialog is non-modal (Radix draws
            no overlay for one) and the step is a short card, so without this
            the page around it stayed live: the tab bar navigated underneath,
            and Live Scan could stack its "Add your trainer name" sheet over
            the form that adds it. Portalled to <body> ahead
            of the content, at the same level (z-50): above the page and the
            tab bar, below the card. The legal splash (z-200) covers both. */}
        {trainerStep && (
          <DialogPortal>
            <div className="fixed inset-0 z-50 bg-black/80" data-testid="trainer-step-backdrop" />
          </DialogPortal>
        )}
        <DialogContent
          className={`sm:max-w-md ${mandatoryMode ? "[&>button.absolute.right-4.top-4]:hidden" : ""} overflow-y-auto v2-neon-card`}
          data-testid="dialog-auth"
          // The trainer step never closes on a tap beside it: a gate has
          // "Not now" (or nothing), and a tap outside to drop the keyboard
          // must not throw away what was typed.
          onInteractOutside={trainerStep ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={mandatoryMode ? (e) => e.preventDefault() : undefined}
          // NO AUTOFOCUS ON A GATE. Radix focuses the first control on mount,
          // which on the trainer step is the name field, and on iOS a focused
          // field raises the keyboard. Straight after sign-up this dialog
          // mounts UNDER the legal splash: the keyboard came up over the
          // disclaimer and covered "I Understand". The trainer taps the field
          // when they get to it. (The sign in form keeps the default.)
          onOpenAutoFocus={mandatoryMode ? (e) => e.preventDefault() : undefined}
          style={
            trainerStep
              ? {
                  background: "var(--surface-dark-7)",
                  // A SHORT CARD, CENTRED IN THE FREE SPACE. The iOS keyboard
                  // overlays the page (Keyboard.resize 'none'), so the free
                  // space runs from under the notch down to the keyboard
                  // (--keyboard-h, set by GlobalKeyboard, 0 with none) or the
                  // home indicator, whichever is higher. The card is centred
                  // in THAT and capped to it, and scrolls inside itself on a
                  // screen too short to hold it. iOS ONLY for the keyboard: on
                  // Android the WebView itself shrinks, so 100dvh is already
                  // the space above the keys, and subtracting --keyboard-h
                  // (which the plugin reports there too) a second time left a
                  // sliver. The insets are kept OUT of the card rather than
                  // added to its padding: padding a centred card by the notch
                  // and the home indicator made it some 90px taller on a
                  // phone than its content, for nothing.
                  top: `calc(var(--nx-safe-top, 0px) + (100dvh - var(--nx-safe-top, 0px) - ${bottomRoom}) / 2)`,
                  maxHeight: `calc(100dvh - var(--nx-safe-top, 0px) - ${bottomRoom} - 1rem)`,
                  padding: "1.25rem",
                }
              : {
                  background: "var(--surface-dark-7)",
                  // The sign in / sign up form is tall: centre and cap it in
                  // the space above the iOS keyboard (as above), and pad both
                  // safe-area insets so the heading clears the notch and the
                  // last control clears the home indicator.
                  top: `calc((100dvh - ${keyboardRoom}) / 2)`,
                  maxHeight: `calc(100dvh - ${keyboardRoom})`,
                  paddingTop: "max(1.5rem, var(--nx-safe-top))",
                  paddingBottom: "max(1.5rem, var(--nx-safe-bottom))",
                }
          }
        >
          {trainerStep ? (
            <>
              <TrainerSetup
                onComplete={handleProfileComplete}
                kind={stepKind}
                onDefer={
                  mandatoryMode && deferrable
                    ? () => {
                        // Closes the step until the app is next opened; Live
                        // Scan's "Add my trainer name" opens it again whenever
                        // they are ready.
                        dismissProfileSetupRequest(account?.id);
                        deferTrainerConfirm(account?.id);
                        closeAuth();
                      }
                    : undefined
                }
              />
            </>
          ) : (
            <>
              <div className="flex justify-center mb-2">
                <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />WELCOME</span>
              </div>
              <DialogHeader>
                <DialogTitle className="v2-display text-xl tracking-tight" style={{ color: "hsl(142 70% 96%)" }}>
                  {isSignup ? "Sign up and bring your roster in." : "Welcome back, trainer."}
                </DialogTitle>
                <DialogDescription style={{ color: "hsl(142 18% 72%)" }}>
                  {isSignup
                    ? "One account, phone and web portal in sync."
                    : "Use the same account on phone and web portal."}
                </DialogDescription>
              </DialogHeader>

              {/* OAuth buttons */}
              <div className="space-y-2 mt-3">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={oauthBusy !== null}
                  data-testid="button-continue-google"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
                  style={{
                    background: "var(--surface-dark-9)",
                    border: "1px solid hsl(142 80% 35% / 0.55)",
                    color: "hsl(142 70% 96%)",
                  }}
                >
                  <GoogleIcon />
                  {oauthBusy === "google" ? "Connecting..." : "Continue with Google"}
                </button>
                {/* Hidden on Android — Apple Sign In is not wired there. See
                    isAppleSignInAvailable(). */}
                {isAppleSignInAvailable() && (
                  <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={oauthBusy !== null}
                    data-testid="button-continue-apple"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
                    style={{
                      background: "#000",
                      border: "1px solid hsl(142 80% 35% / 0.4)",
                      color: "#fff",
                    }}
                  >
                    <AppleIcon />
                    {oauthBusy === "apple" ? "Connecting..." : "Continue with Apple"}
                  </button>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="flex-1 border-t border-border" />
              </div>

              <form onSubmit={submit} className="space-y-3">
                {/* The trainer name and level are asked for straight after
                    this form, as two typed fields (TrainerSetup), so Google,
                    Apple and email sign-ups all pass through the same step.
                    This form collects email + password only. */}
                <div>
                  <Label htmlFor="auth-email" className="text-xs">Email</Label>
                  <Input
                    id="auth-email"
                    data-testid="input-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="auth-password" className="text-xs">Password</Label>
                  <Input
                    id="auth-password"
                    data-testid="input-password"
                    type="password"
                    autoComplete={isSignup ? "new-password" : "current-password"}
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  {isSignup && <p className="text-[11px] text-muted-foreground mt-1">8 characters or more.</p>}
                </div>

                {!isSignup && (
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground transition"
                      onClick={() => {
                        handleClose();
                        window.location.hash = "#/auth/forgot-password";
                      }}
                      data-testid="button-forgot-password"
                    >
                      Forgot your password?
                    </button>
                  </div>
                )}

                {isSignup && (
                  <div className="rounded-lg border border-border p-3 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs">
                        <p className="font-semibold">I am under 13 years old</p>
                        <p className="text-muted-foreground">A grown-up needs to help with this account.</p>
                      </div>
                      <Switch
                        checked={isUnder13}
                        onCheckedChange={setIsUnder13}
                        data-testid="switch-under-13"
                      />
                    </div>
                    {isUnder13 && (
                      <div className="flex items-center justify-between gap-3 pt-1">
                        <p className="text-xs flex-1">A parent or guardian has approved this account.</p>
                        <Switch
                          checked={parentalConsent}
                          onCheckedChange={setParentalConsent}
                          data-testid="switch-parental-consent"
                        />
                      </div>
                    )}
                    {isUnder13 && (
                      <p className="text-[11px] text-muted-foreground">
                        Research-mode is turned off for under-13 accounts and cannot be turned back on from this device.
                      </p>
                    )}
                  </div>
                )}

                {error && (
                  <div
                    className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-2"
                    data-testid="block-auth-error"
                  >
                    <p className="text-xs text-destructive" data-testid="text-auth-error">
                      {error}
                    </p>
                    {signinNotFound && !isSignup && (
                      <>
                        <p className="text-[11px] text-muted-foreground">
                          New here? You can create an account with this email and password.
                        </p>
                        <button
                          type="button"
                          onClick={switchToSignupKeepingEntries}
                          className="text-xs font-semibold text-primary underline-offset-2 hover:underline"
                          data-testid="button-treat-as-signup"
                        >
                          Create account with this email instead
                        </button>
                      </>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="button-auth-submit"
                  className="v2-neon-cta w-full py-3 inline-flex items-center justify-center disabled:opacity-60"
                >
                  {busy ? "Working" : isSignup ? "Create account" : "Sign in"}
                </button>

                <button
                  type="button"
                  className="w-full text-xs text-muted-foreground hover:text-foreground transition"
                  onClick={() => {
                    setError(null);
                    openAuth(isSignup ? "signin" : "signup");
                  }}
                  data-testid="button-toggle-auth-mode"
                >
                  {isSignup ? "Already have an account? Sign in" : "New here? Create an account"}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
